export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockedAt?: number; // timestamp
  category: 'combat' | 'survival' | 'progression' | 'special';
}

export interface AchievementProgress {
  [key: string]: Achievement;
}

export class Achievements {
  private static readonly STORAGE_KEY = 'rogue-cats-achievements';
  private achievements: Map<string, Achievement>;
  private scene?: Phaser.Scene;

  constructor(scene?: Phaser.Scene) {
    this.scene = scene;
    this.achievements = this.initializeAchievements();
    this.loadProgress();
  }

  private initializeAchievements(): Map<string, Achievement> {
    const achievements = new Map<string, Achievement>();

    // Combat Achievements
    achievements.set('first_blood', {
      id: 'first_blood',
      name: 'First Blood',
      description: 'Defeat your first enemy',
      icon: '🐭',
      unlocked: false,
      category: 'combat'
    });

    achievements.set('mouse_hunter', {
      id: 'mouse_hunter',
      name: 'Mouse Hunter',
      description: 'Defeat 100 enemies',
      icon: '⚔️',
      unlocked: false,
      category: 'combat'
    });

    achievements.set('mouse_slayer', {
      id: 'mouse_slayer',
      name: 'Mouse Slayer',
      description: 'Defeat 500 enemies in a single run',
      icon: '🗡️',
      unlocked: false,
      category: 'combat'
    });

    achievements.set('exterminator', {
      id: 'exterminator',
      name: 'Exterminator',
      description: 'Defeat 1000 enemies total',
      icon: '💀',
      unlocked: false,
      category: 'combat'
    });

    achievements.set('boss_killer', {
      id: 'boss_killer',
      name: 'Boss Killer',
      description: 'Defeat your first boss',
      icon: '👑',
      unlocked: false,
      category: 'combat'
    });

    // Survival Achievements
    achievements.set('survivor', {
      id: 'survivor',
      name: 'Survivor',
      description: 'Survive for 5 minutes',
      icon: '⏱️',
      unlocked: false,
      category: 'survival'
    });

    achievements.set('endurance_cat', {
      id: 'endurance_cat',
      name: 'Endurance Cat',
      description: 'Survive for 10 minutes',
      icon: '⌛',
      unlocked: false,
      category: 'survival'
    });

    achievements.set('unstoppable', {
      id: 'unstoppable',
      name: 'Unstoppable',
      description: 'Survive for 15 minutes',
      icon: '🏆',
      unlocked: false,
      category: 'survival'
    });

    achievements.set('close_call', {
      id: 'close_call',
      name: 'Close Call',
      description: 'Survive with less than 10% health',
      icon: '❤️‍🩹',
      unlocked: false,
      category: 'survival'
    });

    // Progression Achievements
    achievements.set('learning_cat', {
      id: 'learning_cat',
      name: 'Learning Cat',
      description: 'Reach level 10',
      icon: '📚',
      unlocked: false,
      category: 'progression'
    });

    achievements.set('power_cat', {
      id: 'power_cat',
      name: 'Power Cat',
      description: 'Reach level 25',
      icon: '⭐',
      unlocked: false,
      category: 'progression'
    });

    achievements.set('legendary_cat', {
      id: 'legendary_cat',
      name: 'Legendary Cat',
      description: 'Reach level 50',
      icon: '🌟',
      unlocked: false,
      category: 'progression'
    });

    achievements.set('arsenal', {
      id: 'arsenal',
      name: 'Arsenal',
      description: 'Equip 4 different weapons in one run',
      icon: '🎯',
      unlocked: false,
      category: 'progression'
    });

    achievements.set('fully_armed', {
      id: 'fully_armed',
      name: 'Fully Armed',
      description: 'Max out a weapon to level 8',
      icon: '🔫',
      unlocked: false,
      category: 'progression'
    });

    // Special Achievements
    achievements.set('persistent', {
      id: 'persistent',
      name: 'Persistent',
      description: 'Complete 10 runs',
      icon: '🎮',
      unlocked: false,
      category: 'special'
    });

    achievements.set('collector', {
      id: 'collector',
      name: 'Collector',
      description: 'Collect 1000 XP gems',
      icon: '💎',
      unlocked: false,
      category: 'special'
    });

    achievements.set('treat_hoarder', {
      id: 'treat_hoarder',
      name: 'Treat Hoarder',
      description: 'Earn 500 Cat Treats total',
      icon: '🍪',
      unlocked: false,
      category: 'special'
    });

    achievements.set('power_hungry', {
      id: 'power_hungry',
      name: 'Power Hungry',
      description: 'Collect 10 power-ups in one run',
      icon: '✨',
      unlocked: false,
      category: 'special'
    });

    return achievements;
  }

  private loadProgress(): void {
    const saved = localStorage.getItem(Achievements.STORAGE_KEY);
    if (saved) {
      try {
        const progress: AchievementProgress = JSON.parse(saved);
        for (const [id, data] of Object.entries(progress)) {
          const achievement = this.achievements.get(id);
          if (achievement && data.unlocked) {
            achievement.unlocked = data.unlocked;
            achievement.unlockedAt = data.unlockedAt;
          }
        }
      } catch (e) {
        console.error('Failed to load achievements:', e);
      }
    }
  }

  private save(): void {
    const progress: AchievementProgress = {};
    this.achievements.forEach((achievement, id) => {
      progress[id] = { ...achievement };
    });
    localStorage.setItem(Achievements.STORAGE_KEY, JSON.stringify(progress));
  }

  public unlock(achievementId: string): boolean {
    const achievement = this.achievements.get(achievementId);
    if (!achievement || achievement.unlocked) return false;

    achievement.unlocked = true;
    achievement.unlockedAt = Date.now();
    this.save();

    // Show notification if scene is available
    if (this.scene) {
      this.showAchievementNotification(achievement);
    }

    return true;
  }

  private showAchievementNotification(achievement: Achievement): void {
    if (!this.scene) return;

    const centerX = this.scene.cameras.main.centerX;
    const startY = 100;

    // Background
    const bg = this.scene.add.rectangle(centerX, startY, 400, 80, 0x000000, 0.9);
    bg.setStrokeStyle(4, 0xFFD700);
    bg.setScrollFactor(0);
    bg.setDepth(10000);

    // Achievement unlocked text
    const headerText = this.scene.add.text(centerX, startY - 20, 'ACHIEVEMENT UNLOCKED!', {
      fontSize: '16px',
      color: '#FFD700',
      fontStyle: 'bold'
    });
    headerText.setOrigin(0.5);
    headerText.setScrollFactor(0);
    headerText.setDepth(10001);

    // Achievement details
    const detailsText = this.scene.add.text(centerX, startY + 5, `${achievement.icon} ${achievement.name}`, {
      fontSize: '20px',
      color: '#FFFFFF',
      fontStyle: 'bold'
    });
    detailsText.setOrigin(0.5);
    detailsText.setScrollFactor(0);
    detailsText.setDepth(10001);

    const descText = this.scene.add.text(centerX, startY + 25, achievement.description, {
      fontSize: '14px',
      color: '#CCCCCC',
      fontStyle: 'italic'
    });
    descText.setOrigin(0.5);
    descText.setScrollFactor(0);
    descText.setDepth(10001);

    // Animate in
    const elements = [bg, headerText, detailsText, descText];
    elements.forEach(element => {
      element.setAlpha(0);
      element.setY(element.y - 30);
    });

    this.scene.tweens.add({
      targets: elements,
      alpha: 1,
      y: '+=30',
      duration: 500,
      ease: 'Back.out'
    });

    // Animate out and destroy
    this.scene.time.delayedCall(4000, () => {
      this.scene!.tweens.add({
        targets: elements,
        alpha: 0,
        y: '-=30',
        duration: 500,
        onComplete: () => {
          elements.forEach(element => element.destroy());
        }
      });
    });
  }

  public getAchievement(id: string): Achievement | undefined {
    return this.achievements.get(id);
  }

  public getAllAchievements(): Achievement[] {
    return Array.from(this.achievements.values());
  }

  public getUnlockedCount(): number {
    return Array.from(this.achievements.values()).filter(a => a.unlocked).length;
  }

  public getTotalCount(): number {
    return this.achievements.size;
  }

  public getCompletionPercentage(): number {
    return Math.floor((this.getUnlockedCount() / this.getTotalCount()) * 100);
  }

  public getAchievementsByCategory(category: string): Achievement[] {
    return Array.from(this.achievements.values()).filter(a => a.category === category);
  }
}
